import React, { useState, useRef, useEffect } from 'react'
import { MyModal } from '~/components/ui/MyModal'
import styled from 'styled-components'
import { QRCodeSVG } from 'qrcode.react'
import {
  toCDNImage,
  xhrDownload,
  to400Image,
  preLoadImgs,
  toTransparentBg,
} from '~/utils'
import domtoimage from 'dom-to-image'
import { CDNImage } from '~/components/business/CDNImage'
import { CopyText } from './CopyText'
import styles from './ArtworkShareModal.module.css'

const StyledAction = styled.div<{ dark?: boolean }>`
  display: flex;
  justify-content: center;
  margin: 24px auto 0;
  font-size: 12px;
  line-height: 20px;
  img {
    margin-bottom: 8px;
  }
  .action-item {
    width: 54px;
    cursor: pointer;
    text-align: center;
    color: ${props =>
      props.dark ? props.theme['neutral-800'] : props.theme['neutral-300']};
    & + .action-item {
      margin-left: 36px;
    }
  }
`

const CustomMyModal = styled(MyModal)`
  .ant-modal-body {
    padding: 32px 36px;
  }
`

interface ShareOptions {
  type: 'ai' | 'match' | 'christmas' | 'plaza'
  author: string
  title?: string
  imgUrl: string
  id: number
  link: string
  copyUrl: string
  /** 底图 */
  initImageUrl?: string
}

interface Props {
  visible: boolean
  setVisible: React.Dispatch<React.SetStateAction<boolean>>
  options: ShareOptions
  dark?: boolean
  isVideo?: boolean
}

const typeMap = {
  match: {
    bg: '/images/share/share-bg@2x.png',
    logo: '/images/share/logo-ai-v2@2x.png',
    titleImg: '/images/share/title-match-v2@2x.png',
    textImg: '/images/share/text-match@2x.png',
    text: '扫码投票',
  },
  // ai大赛作品详情-定制圣诞主题
  christmas: {
    bg: '/images/share/share_bg_christmas@2x.png',
    logo: '/images/share/logo_christmas@2x.png',
    titleImg: '/images/share/title_christmas@2x.png',
    textImg: '/images/share/text-match@2x.png',
    text: '扫码投票',
  },
  ai: {
    bg: '/images/share/share-bg@2x.png',
    logo: '/images/share/logo-ai-v2@2x.png',
    titleImg: '/images/share/title-default-v2@2x.png',
    textImg: '/images/share/text-ai@2x.png',
    text: '免费作画',
  },
  plaza: {
    bg: '/images/share/share-bg@2x.png',
    logo: '/images/share/logo-ai-v2@2x.png',
    titleImg: '/images/share/title-default-v2@2x.png',
    textImg: '/images/share/text-plaza@2x.png',
    text: '一键同款',
  },
}

export const ArtworkShareModal = ({
  visible,
  setVisible,
  options,
  dark = false,
  isVideo,
}: Props) => {
  const ID = 'share-product'
  const [imgUrl, setImgUrl] = useState('')
  // 需要监听加载的图片总数量
  const totalLoadImgCount = useRef(0)
  // 加载完成的图片数量
  const loadSuccessImgCount = useRef(0)

  const onDownload = async () => {
    xhrDownload(imgUrl, (options?.title || 'AI分享') as string)
    setVisible(false)
  }

  const createImgUrl = async () => {
    await domtoimage.toJpeg(document.getElementById(ID) as HTMLElement)
    const imgUrl = await domtoimage.toJpeg(
      document.getElementById(ID) as HTMLElement
    )
    setImgUrl(imgUrl)
  }

  /**
   * 监听图片加载
   * tips:
   * dom-to-image是将dom转化成svg，然后转化成canvas。
   * 只有所有图片需要加载完成后才能绘制成功，不然会可能出现第一次渲染图片空白的情况
   */
  const onLoadImage = () => {
    loadSuccessImgCount.current++

    if (loadSuccessImgCount.current === totalLoadImgCount.current) {
      createImgUrl()
    }
  }
  useEffect(() => {
    options?.imgUrl && totalLoadImgCount.current++
    options?.initImageUrl && totalLoadImgCount.current++
  }, [options])

  return (
    <CustomMyModal
      dark={dark}
      open={visible}
      title="分享作品"
      onCancel={() => setVisible(false)}
      maskStyle={{
        backgroundColor: 'rgba(0,0,0,0.8)',
      }}
      width={448}
      footer={null}
      transitionName=""
      maskTransitionName=""
      centered
    >
      <div
        id={ID}
        style={{
          width: 375,
          background: `url(${toCDNImage(
            typeMap[options?.type].bg
          )}) center top no-repeat`,
          backgroundSize: 'cover',
        }}
      >
        <div className="p-3 relative overflow-hidden">
          <CDNImage
            src={typeMap[options?.type].logo}
            className="mb-3 block"
            alt=""
          />
          <div className="relative bg-neutral-900 py-4 px-3">
            <CDNImage
              src={typeMap[options?.type].titleImg}
              className="mb-3 block"
              alt=""
            />
            <div className="border-2 border-neutral-100 relative">
              <div className="relative">
                <img
                  className="w-full"
                  src={options?.imgUrl as string}
                  alt=""
                  style={toTransparentBg({
                    imgUrl: options.imgUrl,
                  })}
                  onLoad={() => {
                    onLoadImage()
                  }}
                />
                {options?.initImageUrl && (
                  <div className="w-[64px] absolute bottom-2 left-2 border-[1px] border-solid border-neutral-900">
                    <img
                      className="relative w-full"
                      src={to400Image(options?.initImageUrl)}
                      alt=""
                      onLoad={() => {
                        onLoadImage()
                      }}
                    />
                    <div className={styles.initImg}>
                      <div className={styles.initImgTxt}>生成前</div>
                    </div>
                  </div>
                )}
              </div>
              {/* 外部帖子没有title和author信息，隐藏这个模块， || 兼容下ai作品无title分享 */}
              {!!(options?.title || options?.author) && (
                <div className="flex items-center justify-between px-2 py-2">
                  <div className="w-[150px]">
                    {!!options?.title && (
                      <div className="text-sm text-neutral-200 overflow-hidden text-ellipsis whitespace-nowrap">
                        {options?.title}
                      </div>
                    )}
                    <div className="text-neutral-200 text-xs overflow-hidden text-ellipsis whitespace-nowrap">
                      <span className="">创作者：</span>
                      <span className="text-neutral-100 font-bold">
                        {options?.author}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <CDNImage
                      className="h-11 mr-1"
                      src={typeMap[options?.type].textImg}
                      alt=""
                    />
                    <div className="w-11 h-11">
                      <QRCodeSVG
                        value={options?.link as string}
                        size={44}
                        style={{
                          display: 'block',
                        }}
                      ></QRCodeSVG>
                    </div>
                  </div>
                </div>
              )}
              {/* 分享视频 */}
              {isVideo && (
                <div
                  className="absolute top-2 right-2 w-[56px] h-6 p-[1px] rounded-[1000px] backdrop-blur-sm"
                  style={{
                    background:
                      'linear-gradient(0deg, rgba(0, 0, 0, 0.64), rgba(255, 255, 255, 1)),linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)',
                  }}
                >
                  <div className="w-full h-full rounded-[1000px] justify-center flex items-center bg-black bg-opacity-75">
                    <img
                      src="/images/icons/play.png"
                      className="w-3 h-3 mr-[2px]"
                      alt=""
                    />
                    <span className="text-xs text-neutral-900">视频</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <StyledAction dark={dark}>
        <div className="action-item" onClick={onDownload}>
          <img src={toCDNImage('/images/common/download.svg')} alt="" />
          保存图片
        </div>
        <div className="action-item">
          <CopyText text={options.copyUrl}>
            <img src={toCDNImage('/images/common/link.svg')} alt="" />
            {options.type === 'ai' ? '图片链接' : '复制链接'}
          </CopyText>
        </div>
      </StyledAction>
    </CustomMyModal>
  )
}
